import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { createApiClient, apiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../api/apiClient';
import { useToast } from '../../../components/LhToastContainer';
import { setToastNotification } from '../../../store/slices/actionPlanSlice';
import { logger } from '../../../utils/logger';
import { handleActionPlanErrorWithFriendlyMessage, ACTION_PLAN_API_NAMES } from '../../../store/utils/actionPlanErrorHandler';
import {
  SOURCE_OPTIONS,
  TYPE_OPTIONS,
  PRIORITY_OPTIONS,
  SOURCE_TO_BACKEND_MAPPING,
  transformUserToAssigneeOption,
  transformEmployeeToOption,
  findMatchingOption,
  emailToDisplayName,
  formatAuthUserName,
  getSourceApiValue,
  getTypeApiValue,
} from '../utils/formOptions';

export const useCreateActionPlanForm = (
  data: any = {},
  type: 'create' | 'edit' | 'view' = 'create',
  onSubmit?: (result: any) => void,
  options: { sendAreaInPayload?: boolean; area?: any; standupSessionId?: string; standupId?: string; isEditMode?: boolean; isTopOpportunity?: boolean; capabilities?: any[]; isContinuousImprovement?: boolean; isLighthouseExecution?: boolean; isSparcKpi?: boolean } = {}
) => {
  const { sendAreaInPayload = false, standupSessionId, standupId, isEditMode, isTopOpportunity, capabilities, isContinuousImprovement, isLighthouseExecution, isSparcKpi } = options;
  const area = options.area;
  // Original data for tracking modifications (in edit mode)
  const [originalData, setOriginalData] = useState<any>(data);

  // Form field states
  const [title, setTitle] = useState<string>(data.title || '');
  const [selectedSource, setSelectedSource] = useState<any>(data.source || null);
  const [selectedType, setSelectedType] = useState<any>(data.type || null);
  const [selectedPriority, setSelectedPriority] = useState<any>(data.priority || null);
  const [selectedOwner, setSelectedOwner] = useState<any>(data.owner || null);
  const [selectedAssignee, setSelectedAssignee] = useState<any>(data.assignee ?? null);
  const [startDate, setStartDate] = useState<Date | null>(data.startDate || null);
  const [targetDate, setTargetDate] = useState<Date | null>(data.targetDate || null);
  const [actionPlan, setActionPlan] = useState<string>(data.actionPlan || '');
  const [notes, setNotes] = useState<string>(data.notes || '');
  const [frontlineInvolvement, setFrontlineInvolvement] = useState<boolean>(!!data.frontlineInvolvement);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(data.employee || null);
  // Area selection (single-select). Initialize from data.area or first capability.question_title
  const initialArea = (() => {
    if (!data) return null;
    if (data.area) return data.area;
    if (Array.isArray(data.capabilities) && data.capabilities.length > 0) {
      const first = data.capabilities[0];
      return first?.question_title ?? first?.area ?? first?.value ?? first?.displayText ?? first?.name ?? null;
    }
    return null;
  })();
  const [selectedArea, setSelectedArea] = useState<any>(initialArea);
  const [areaOptions, setAreaOptions] = useState<any[]>([]);

  // Loading and error states
  const [loading, setLoading] = useState<boolean>(false);
  const [dateValidationError, setDateValidationError] = useState<string>('');
  const [submissionError, setSubmissionError] = useState<any>(null);
  // Per-field validation errors returned from backend (key -> message)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // API data states
  const [userInfoFromAccessApi, setUserInfoFromAccessApi] = useState<any | null>(null);
  const [assigneeList, setAssigneeList] = useState<any[]>([]);
  const [assigneeLoading, setAssigneeLoading] = useState<boolean>(false);
  const [employeeList, setEmployeeList] = useState<any[]>([]);
  const [employeeLoading, setEmployeeLoading] = useState<boolean>(false);

  // Redux selectors
  const selectedLocationFromRedux = useSelector((state: RootState) => state.appData.selectedLocation);
  const periodWeek = useSelector((state: RootState) => state.appData.periodWeek);
  const authUserName = useSelector((state: RootState) => state.auth?.user?.name ?? state.auth?.user?.claims?.name ?? state.auth?.user?.username ?? '');
  const authUserId = useSelector((state: RootState) => state.auth?.user?.gpid ?? state.auth?.user?.id ?? null);
  const userInfoGPID = useSelector((state: RootState) => state.appData?.userInfo?.GPID ?? null);
  const fieldUsers = useSelector((state: RootState) => state.appData?.FieldUsers ?? []);
  const coeUsers = useSelector((state: RootState) => state.appData?.COEUsers ?? []);
  const userRoleName = useSelector((state: RootState) => state.appData?.userInfo?.roleName ?? '');

  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();

  const isView = type === 'view';
  const isCreateDisabled = title.trim() === '' || actionPlan.trim() === '' || loading;
  const ownerOption = useMemo(() => [{ id: authUserId ?? 'owner', displayText: formatAuthUserName(authUserName) || '--' }], [authUserId, authUserName]);

  // Get email addresses from user data
  const authUserEmail = useSelector((state: RootState) => state.auth?.user?.username ?? '');
  const action_plan_owner_email = userInfoFromAccessApi?.email ?? userInfoFromAccessApi?.username ?? authUserEmail ?? '';
  const assignee_email = selectedAssignee?.email ?? selectedAssignee?.username ?? selectedAssignee?.mail ?? '';
  const created_by_email = authUserEmail ?? '';
  
  // Get action plan owner GPID from appData
  const action_plan_owner_gpid = userInfoGPID ?? authUserId ;

  // Format date to YYYY-MM-DD for API
  const formatISODate = (d: Date | null) => {
    if (!d) return null;
    try {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    } catch (e) {
      return null;
    }
  };

  // Import UTC-safe date formatting utility
  const { formatDateForDisplay } = require('../utils/dateUtils');
  
  // Format date for display WITHOUT timezone conversion
  const formatDisplayDate = (d: Date | null) => {
    return formatDateForDisplay(d);
  };

  // Reset form for create type - only run when type changes
  useEffect(() => {
    console.log('[useCreateActionPlanForm] Reset effect running', { type, hasSource: !!data.source, hasType: !!data.type });
    
    if (type === 'create') {
      console.log('[useCreateActionPlanForm] Type is create, initializing form');
      setTitle(data.title || '');
      setSelectedSource(data.source || null);
      setSelectedType(data.type || null);
      setSelectedPriority(data.priority || null);
      setSelectedOwner(data.owner || null);
      setSelectedAssignee(data.assignee || null);
      setStartDate(data.startDate || null);
      setTargetDate(data.targetDate || null);
      setActionPlan(data.actionPlan || '');
      setNotes(data.notes || '');
      setFrontlineInvolvement(!!data.frontlineInvolvement);
      setSelectedEmployee(data.employee || null);
      
      // Initialize area from data or capabilities array if provided
      try {
        if (data.area) {
          setSelectedArea(Array.isArray(data.area) ? (data.area[0] ?? null) : data.area);
        } else if (Array.isArray(data.capabilities) && data.capabilities.length > 0) {
          const first = data.capabilities[0];
          const areaVal = first?.question_title ?? first?.area ?? first?.value ?? first?.displayText ?? first?.name ?? null;
          setSelectedArea(areaVal ?? null);
        } else {
          setSelectedArea(null);
        }
      } catch (e) {
        console.error('Failed to initialize selectedArea from data.capabilities', e);
      }
      
      setSubmissionError(null);
      setValidationErrors({});
      setOriginalData(data);
      return;
    }

    // For edit/view, populate from provided data and store as original
    setTitle(data.title || '');
    setSelectedSource(data.source || null);
    setSelectedType(data.type || null);
    setSelectedPriority(data.priority || null);
    setSelectedOwner(data.owner || null);
    setSelectedAssignee(data.assignee || null);
    setStartDate(data.startDate || null);
    setTargetDate(data.targetDate || null);
    setActionPlan(data.actionPlan || '');
    setNotes(data.notes || '');
    setFrontlineInvolvement(!!data.frontlineInvolvement);
    setSelectedEmployee(data.employee || null);
    // Initialize area from data or capabilities array if provided
    try {
      if (data.area) {
        setSelectedArea(Array.isArray(data.area) ? (data.area[0] ?? null) : data.area);
      } else if (Array.isArray(data.capabilities) && data.capabilities.length > 0) {
        // capabilities may contain area info; prefer `question_title` from the first capability
        const first = data.capabilities[0];
        const areaVal = first?.question_title ?? first?.area ?? first?.value ?? first?.displayText ?? first?.name ?? null;
        setSelectedArea(areaVal ?? null);
      } else {
        setSelectedArea(null);
      }
    } catch (e) {
      console.error('Failed to initialize selectedArea from data.capabilities', e);
    }
    setOriginalData(data);
  }, [type]);

  // Fetch user's location-and-access data
  useEffect(() => {
    let mounted = true;
    const fetchLocationAccess = async () => {
      try {
        const api = createApiClient();
        const res = await api.post('/common/location-with-access');
        const ui = res?.data?.data?.userInfo ?? res?.data?.userInfo ?? null;
        if (mounted) setUserInfoFromAccessApi(ui);
      } catch (err) {
        console.error('Failed to fetch location-with-access', err);
        // Dispatch centralized ActionPlan error so overlay/modal can surface this failure
        try {
          handleActionPlanErrorWithFriendlyMessage(ACTION_PLAN_API_NAMES.FETCH_LOCATION_WITH_ACCESS, err, true, { api: '/common/location-with-access' });
        } catch (handlerErr) {
          console.error('Failed to dispatch centralized ActionPlan error for location-with-access:', handlerErr);
        }
        if (mounted) setUserInfoFromAccessApi(null);
      }
    };
    fetchLocationAccess();
    return () => {
      mounted = false;
    };
  }, []);

  // Populate areaOptions from provided data.capabilities if available
  useEffect(() => {
    if (Array.isArray(originalData?.capabilities) && originalData.capabilities.length > 0) {
      try {
        const opts = originalData.capabilities.map((c: any) => {
          const id = c.id ?? c.question_title ?? c.area ?? c.value ?? c.name ?? c.displayText ?? null;
          const displayText = c.question_title ?? c.displayText ?? c.name ?? c.area ?? c.value ?? String(id);
          return id ? { id, displayText } : null;
        }).filter(Boolean);
        setAreaOptions(opts as any[]);
      } catch (e) {
        setAreaOptions([]);
      }
    } else {
      setAreaOptions([]);
    }
  }, [originalData?.capabilities]);

  // Helper to deduplicate option arrays by id/displayText (case-insensitive)
  const dedupeOptions = (options: any[] = []) => {
    const map = new Map<string, any>();
    for (const opt of options) {
      const key = String(opt?.id ?? opt?.displayText ?? '').trim().toLowerCase();
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, opt);
      }
    }
    return Array.from(map.values());
  };

  const [prevLocation, setPrevLocation] = useState<string | null>(null);


  useEffect(() => {
    let mounted = true;
    
    const locParam =
      selectedLocationFromRedux?.displayText ??
      selectedLocationFromRedux?.name ??
      selectedLocationFromRedux?.locationName ??
      selectedLocationFromRedux?.location ??
      selectedLocationFromRedux?.id ??
      '';

    if (!locParam) {
      if (mounted) setAssigneeList([]);
      return;
    }

    // Clear selected assignee when location changes (only in create mode)
    if (type === 'create' && prevLocation !== null && prevLocation !== locParam) {
      setSelectedAssignee(null);
    }
    
    // Update previous location tracker
    if (mounted) setPrevLocation(locParam);

    setAssigneeLoading(true);
    try {
      // Combine FieldUsers and COEUsers from Redux
      let combinedUsers = [...(fieldUsers || []), ...(coeUsers || [])];
      
      // If user is NOT a Super User, filter out Super Users from the list
      if (userRoleName && userRoleName !== 'Super User') {
        combinedUsers = combinedUsers.filter((user: any) => 
          user.Role !== 'Super User'
        );
      }
      
      // Helper function to extract name from email or user fields
      const extractDisplayName = (user: any): string => {
        // Priority 1: Check for explicit name fields
        if (user.First_Name && user.Last_Name) {
          return `${user.First_Name} ${user.Last_Name}`.trim();
        }
        
        if (user.Name && typeof user.Name === 'string' && user.Name.trim()) {
          // Remove "Contractor" suffix if present
          return user.Name.trim().replace(/\s*Contractor\s*$/i, '').trim();
        }
        
        // Priority 2: Extract name from email (e.g., "adrian.conde@pepsico.com" -> "Adrian Conde")
        if (user.UserEmail && typeof user.UserEmail === 'string') {
          const emailPart = user.UserEmail.split('@')[0]; // Get part before @
          if (emailPart) {
            // Replace dots with spaces and capitalize each word
            const nameFromEmail = emailPart
              .split('.')
              .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
              .join(' ');
            // Remove "Contractor" suffix if present
            if (nameFromEmail) return nameFromEmail.replace(/\s*Contractor\s*$/i, '').trim();
          }
          // Fallback to full email if extraction fails
          return user.UserEmail;
        }
        
        // Priority 3: Return GPID or ID as last resort
        return user.GPID ?? user.ID ?? 'Unknown';
      };
      
      // Transform users to assignee options
      const opts = combinedUsers.map((user: any) => {
        const displayText = extractDisplayName(user);
        
        return {
          id: String(user.GPID ?? user.ID ?? ''),
          displayText: displayText,
          email: user.UserEmail ?? '',
          role: user.Role ?? undefined,
          role_id: user.RoleID ?? undefined,
          raw: user,
        };
      });
      
      const uniqueOpts = dedupeOptions(opts);
      if (mounted) setAssigneeList(uniqueOpts);
    } catch (err) {
      console.error('Failed to populate assignees from Redux', err);
      if (mounted) setAssigneeList([]);
    } finally {
      if (mounted) setAssigneeLoading(false);
    }
    
    return () => {
      mounted = false;
    };
  }, [fieldUsers, coeUsers, userRoleName, selectedLocationFromRedux, type, prevLocation]);

  // Auto-select assignee when list loads
  useEffect(() => {
    if (!assigneeList || assigneeList.length === 0) return;
    
    // Always try to find a match in the assigneeList - this ensures we get the displayText
    const matchAssignee = selectedAssignee || data?.assignee;
    if (matchAssignee) {
      const match = findMatchingOption(assigneeList, matchAssignee);
      if (match) {
        // Update selectedAssignee with the matched option from the list (which has proper displayText)
        setSelectedAssignee(match);
      }
    }
  }, [assigneeList, data?.assignee]);

  useEffect(() => {
    let mounted = true;
    
    const fetchAssigneeDetails = async () => {
      // Only fetch if selectedAssignee has an id but no displayText or displayText is just a GPID
      if (!selectedAssignee?.id) return;
      if (selectedAssignee?.displayText && selectedAssignee.displayText.trim() && !/^\d+$/.test(selectedAssignee.displayText)) {
        // displayText exists and is not a pure GPID number, so we're good
        return;
      }

      try {
        const api = createApiClient();
        // Try to fetch user by GPID
        const response = await api.get(`/users/${selectedAssignee.id}`);
        const userData = response?.data?.data ?? response?.data;
        
        if (userData && mounted) {
          // Create a proper option object with displayText
          const displayText = userData.UserEmail 
            ? emailToDisplayName(userData.UserEmail)
            : (userData.name || selectedAssignee.id);
          
          setSelectedAssignee({
            id: selectedAssignee.id,
            displayText,
            email: userData.UserEmail || '',
            role: userData.Role || '',
            role_id: userData.RoleID || '',
            raw: userData,
          });
        }
      } catch (err) {
        console.error('Failed to fetch assignee details:', err);
        // Keep the current state - don't override with error
      }
    };

    fetchAssigneeDetails();
    return () => {
      mounted = false;
    };
  }, [selectedAssignee?.id]);

  // Fetch employees for selected location (using /nho/employees API)
  useEffect(() => {
    let mounted = true;
    const fetchEmployees = async () => {
      // Clear previous employee list and selection immediately when location changes
      // so the UI doesn't show an employee from the old location while the new list loads.
      if (mounted) {
        setEmployeeList([]);
        setSelectedEmployee(null);
      }
      const locParam =
        selectedLocationFromRedux?.displayText ??
        selectedLocationFromRedux?.name ??
        selectedLocationFromRedux?.locationName ??
        selectedLocationFromRedux?.location ??
        selectedLocationFromRedux?.id ??
        '';

      if (!locParam) { 
        if (mounted) setEmployeeList([]);
        return;
      }

      setEmployeeLoading(true);
      try {
        const api = createApiClient();
        // Using /nho/employees API to get employees with Status field (updated: 2025-12-19)
        const url = `/nho/employees?Location_Name=${encodeURIComponent(String(locParam))}`;
        const resp = await api.get(url);
        // API returns array directly or nested in data
        let items = resp?.data ?? [];
        // If nested in data.data, extract it
        if (items && !Array.isArray(items) && items.data) {
          items = items.data;
        }
        
       
        
        // Ensure items is an array
        if (!Array.isArray(items)) {
          if (mounted) setEmployeeList([]);
          return;
        }
        
        const totalBefore = items.length;
        const filteredItems = items.filter((emp: any) => {
          const status = emp?.Status || emp?.Active_vs_Inactive || '';
          const statusLower = String(status).toLowerCase();
          const isTerminated = statusLower === 'terminated';
          if (isTerminated) {
            logger.log('[ACTION_PLAN_EMPLOYEE] Filtering out terminated:', emp.First_Name, emp.Last_Name, 'Status:', status);
          }
          return !isTerminated;
        });
        
        const totalAfter = filteredItems.length;
        const terminatedCount = totalBefore - totalAfter;
          
        const opts = filteredItems.map((emp: any) => ({
          id: emp.GPID || emp.ID?.toString() || '',
          displayText: `${emp.First_Name || ''} ${emp.Last_Name || ''}`.trim() || emp.GPID || '',
          gpid: emp.GPID,
          firstName: emp.First_Name,
          lastName: emp.Last_Name,
          status: emp.Status,
          jobDescription: emp.Job_Description,
          raw: emp,
        }));
        const uniqueOpts = dedupeOptions(opts);
        if (mounted) setEmployeeList(uniqueOpts);
      } catch (err) {
        console.error('Error fetching NHO employees:', err);
        // Dispatch centralized ActionPlan error so overlay/modal can surface this failure
        try {
          handleActionPlanErrorWithFriendlyMessage(ACTION_PLAN_API_NAMES.FETCH_LIST, err, true, { location: String(locParam) });
        } catch (handlerErr) {
          console.error('Failed to dispatch centralized ActionPlan error for NHO employees:', handlerErr);
        }

        if (mounted) setEmployeeList([]);
      } finally {
        if (mounted) setEmployeeLoading(false);
      }
    };

    fetchEmployees();
    return () => {
      mounted = false;
    };
  }, [selectedLocationFromRedux]);

  // Clear selected employee immediately when location changes so the dropdown
  // doesn't retain a selection from a previous location.
  useEffect(() => {
    // Only clear if there is an existing selection — avoids unnecessary state churn
    if (selectedEmployee) {
      setSelectedEmployee(null);
    }
    // Note: employeeList will be repopulated by the fetch effect above.
  }, [selectedLocationFromRedux]);

  // Auto-select employee when list loads
  useEffect(() => {
    if (!employeeList || employeeList.length === 0) return;
    if (selectedEmployee) return;
    const match = findMatchingOption(employeeList, data?.employee);
    if (match) setSelectedEmployee(match);
  }, [employeeList, data, selectedEmployee]);

  // Helper function to compare dates
  const datesEqual = (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 && !date2) return true;
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
    return d1.getTime() === d2.getTime();
  };

  // Helper function to compare objects/options
  const optionsEqual = (opt1: any, opt2: any): boolean => {
    if (!opt1 && !opt2) return true;
    if (!opt1 || !opt2) return false;
    
    // Compare by id first (most reliable)
    if (opt1.id && opt2.id) {
      return String(opt1.id).toLowerCase() === String(opt2.id).toLowerCase();
    }
    
    // Fallback to displayText comparison
    if (opt1.displayText && opt2.displayText) {
      return String(opt1.displayText).toLowerCase() === String(opt2.displayText).toLowerCase();
    }
    
    // If either has only id, compare ids
    return String(opt1.id || opt1.displayText) === String(opt2.id || opt2.displayText);
  };

  // Helper function to get only modified fields for edit mode
  const getModifiedFields = () => {
    const modified: any = {};

    // Check each field for modifications
    if (title !== (originalData?.title || '')) {
      modified.title = title;
    }

    if (!optionsEqual(selectedSource, originalData?.source)) {
      // Use centralized utility to get API format (with underscores)
      modified.source = getSourceApiValue(selectedSource);
    }

    if (!optionsEqual(selectedType, originalData?.type)) {
      modified.type = getTypeApiValue(selectedType);
    }

    if (!optionsEqual(selectedPriority, originalData?.priority)) {
      modified.priority = selectedPriority?.displayText ?? selectedPriority?.id ?? null;
    }

    if (!optionsEqual(selectedAssignee, originalData?.assignee)) {
      modified.assignee_gpid = selectedAssignee?.gpid ?? selectedAssignee?.id ?? null;
      modified.assignee_name = selectedAssignee?.displayText ?? null;
      modified.assignee_email = selectedAssignee?.email ?? null;
    }

    if (!datesEqual(startDate, originalData?.startDate)) {
      modified.start_date = formatISODate(startDate);
    }

    if (!datesEqual(targetDate, originalData?.targetDate)) {
      modified.target_date = formatISODate(targetDate);
    }

    if (actionPlan !== (originalData?.actionPlan || '')) {
      modified.action_plan_description = actionPlan;
    }

    if (frontlineInvolvement !== !!originalData?.frontlineInvolvement) {
      modified.frontline_employee_involvement = !!frontlineInvolvement;
    }

    if (!optionsEqual(selectedEmployee, originalData?.employee)) {
      modified.employee_gpid = selectedEmployee?.gpid ?? selectedEmployee?.id ?? undefined;
      modified.employee_name = selectedEmployee?.displayText ?? null;
    }

    const areaToCompare = area ?? selectedArea;
    if (sendAreaInPayload && areaToCompare) {
      // Determine original area value (from originalData.area or first capability.question_title)
      const origArea = originalData?.area ?? (Array.isArray(originalData?.capabilities) && originalData.capabilities[0]
        ? (originalData.capabilities[0].question_title ?? originalData.capabilities[0].area ?? null)
        : null);

      if (!optionsEqual(areaToCompare, origArea)) {
        modified.capabilities = [{ question_title: areaToCompare?.id ?? areaToCompare?.displayText ?? areaToCompare }];
      }
    }

    if (notes !== (originalData?.notes || '')) {
      modified.notes = notes || undefined;
    }

    if (type === 'edit') {
      modified.notes = notes || undefined;
    }

    // Check if top_opportunity has changed
    const originalTopOpportunity = originalData?.topOpportunity === 1 || originalData?.topOpportunity === true || false;
    const currentTopOpportunity = isTopOpportunity ?? false;
    if (currentTopOpportunity !== originalTopOpportunity) {
      modified.TOP_OPPORTUNITY = currentTopOpportunity;
      modified.__sendTopOpportunity = true;
    }

    return modified;
  };

  // Handle target date change with validation
  const handleTargetDateChange = (date: Date | null) => {
    setTargetDate(date);

    if (date && startDate) {
      const startDateObj = new Date(startDate);
      const targetDateObj = new Date(date);

      startDateObj.setHours(0, 0, 0, 0);
      targetDateObj.setHours(0, 0, 0, 0);

      if (targetDateObj < startDateObj) {
        setDateValidationError('Target Date must be equal or greater than Start Date');
      } else {
        setDateValidationError('');
      }
    } else {
      setDateValidationError('');
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate dates
    if (startDate && targetDate) {
      const startDateObj = new Date(startDate);
      const targetDateObj = new Date(targetDate);

      startDateObj.setHours(0, 0, 0, 0);
      targetDateObj.setHours(0, 0, 0, 0);

      if (targetDateObj < startDateObj) {
        setDateValidationError('Target Date must be equal or greater than Start Date');
        return;
      }
    }

    // Client-side required fields validation for create
    if (type === 'create') {
      const requiredChecks: { key: string; present: boolean; label: string }[] = [
        { key: 'title', present: Boolean(title && title.trim()), label: 'Title' },
        { key: 'source', present: Boolean(selectedSource), label: 'Source' },
        // Type can be a string (free-text when source is General) or an object (from dropdown)
        { key: 'type', present: typeof selectedType === 'string' ? Boolean(selectedType.trim()) : Boolean(selectedType), label: 'Type' },
        { key: 'priority', present: Boolean(selectedPriority), label: 'Priority' },
        { key: 'assignee', present: Boolean(selectedAssignee && (selectedAssignee.gpid || selectedAssignee.id || selectedAssignee.displayText)), label: 'Assignee' },
        { key: 'start_date', present: Boolean(startDate), label: 'Start Date' },
        { key: 'target_date', present: Boolean(targetDate), label: 'Target Date' },
        { key: 'action_plan_description', present: Boolean(actionPlan && actionPlan.trim()), label: 'Action Plan' },
      ];

      // If frontline involvement checkbox is checked, require employee
      if (frontlineInvolvement) {
        requiredChecks.push({ key: 'employee', present: Boolean(selectedEmployee && (selectedEmployee.gpid || selectedEmployee.id || selectedEmployee.displayText)), label: 'Employee Name' });
      }

      const missing = requiredChecks.filter((c) => !c.present);
      if (missing.length > 0) {
        // Build per-field validationErrors
        const fieldErrs: Record<string, string> = {};
        missing.forEach((m) => {
          fieldErrs[m.key] = `${m.label} is required`;
        });

        // Aggregate assignee related keys into single 'assignee' if backend may produce them
        if (fieldErrs['assignee_gpid'] || fieldErrs['assignee_email'] || fieldErrs['assignee_role'] || fieldErrs['assignee_role_id']) {
          fieldErrs['assignee'] = fieldErrs['assignee'] || 'Assignee is required';
          delete fieldErrs['assignee_gpid'];
          delete fieldErrs['assignee_email'];
          delete fieldErrs['assignee_role'];
          delete fieldErrs['assignee_role_id'];
        }

        // Ensure frontend uses 'employee' key instead of 'employee_gpid'
        if (fieldErrs['employee_gpid']) {
          fieldErrs['employee'] = fieldErrs['employee_gpid'];
          delete fieldErrs['employee_gpid'];
        }

        setValidationErrors((prev) => ({ ...prev, ...fieldErrs }));

        const friendlyNames = missing.map((m) => m.label);
        const banner = `Required fields are missing: ${friendlyNames.join(', ')}`;

        setSubmissionError({ title: 'Validation Error', message: banner, details: null });

        dispatch(
          setToastNotification({
            header: 'Validation Error',
            subtext: banner,
            variant: 'error',
            position: 'top',
            duration: 4000,
          })
        );

        return { success: false, error: 'Validation failed: missing required fields' };
      }
    }

    setDateValidationError('');
    setLoading(true);

    try {
      // For edit mode, only send modified fields
      let payload: any;

      if (type === 'edit') {
        // Get only modified fields
        payload = getModifiedFields();
        
        // If no fields were modified, show a message and return
        if (Object.keys(payload).length === 0) {
          toast.showToast({
            header: 'Info',
            subtext: 'No changes detected',
            variant: 'info',
            position: 'top',
            duration: 4000,
          });
          setLoading(false);
          return { success: false, error: 'No changes detected' };
        }
      } else {
        // For create mode, send all required fields
        payload = {
          title: title,
          source: getSourceApiValue(selectedSource),
          type: getTypeApiValue(selectedType),
          priority: selectedPriority?.displayText ?? selectedPriority?.id ?? null,
          action_plan_owner_gpid: action_plan_owner_gpid,
          action_plan_owner_name: formatAuthUserName(authUserName) || null,
          action_plan_owner_role: userInfoFromAccessApi?.roleName ?? selectedOwner?.role ?? selectedOwner?.role_name ?? null,
          action_plan_owner_role_id: userInfoFromAccessApi?.roleId ?? selectedOwner?.role_id ?? selectedOwner?.roleId ?? null,
          assignee_gpid: selectedAssignee?.gpid ?? selectedAssignee?.id ?? null,
          assignee_name: selectedAssignee?.displayText ?? null,
          assignee_role: selectedAssignee?.role ?? selectedAssignee?.role_name ?? null,
          assignee_role_id: selectedAssignee?.role_id ?? selectedAssignee?.roleId ?? null,
          location: selectedLocationFromRedux?.displayText || selectedLocationFromRedux?.name || '',
          period: periodWeek?.data?.period ?? null,
          week: periodWeek?.data?.week ?? null,
          start_date: formatISODate(startDate),
          target_date: formatISODate(targetDate),
          action_plan_description: actionPlan,
          frontline_employee_involvement: !!frontlineInvolvement,
          employee_gpid: selectedEmployee?.gpid ?? selectedEmployee?.id ?? undefined,
          employee_name: selectedEmployee?.displayText ?? null,
          // include area/capabilities when provided and allowed by caller (single-select)
          capabilities: capabilities && capabilities.length > 0 ? capabilities : (sendAreaInPayload && selectedArea ? [{ question_title: selectedArea?.id ?? selectedArea?.displayText ?? selectedArea }] : undefined),
          created_by: action_plan_owner_gpid,
          created_by_role_id: userInfoFromAccessApi?.roleId ?? selectedOwner?.role_id ?? selectedOwner?.roleId ?? null,
          notes: notes || undefined,
          action_plan_owner_email,
          assignee_email,
          created_by_email,
          clientTimestamp: getLocalTimestamp(),
        };
      }

      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      const api = createApiClient();
      
      // For edit mode, just pass the modified payload to parent via onSubmit
      // Parent (handleSaveEdit) will make the API call
      if (type === 'edit') {
        console.log('Edit mode: Passing modified payload to parent:', payload);
        
        // Call the onSubmit callback with the modified payload
        if (onSubmit) {
          onSubmit(payload);
        }
        
        setLoading(false);
        return { success: true, data: payload };
      }
      
      // For create mode from standup/lighthouse task, use the new /action-plans/source endpoint
      // Otherwise use the old /action-plans endpoint
      let endpoint = '/action-plans';
      let finalPayload = payload;
      
      if (standupSessionId) {
        // Creating from standup/lighthouse task - use new API format
        endpoint = '/action-plans/source';
        
        // Transform source values for API
        let sourceValue = payload.source;
        if (isContinuousImprovement) {
          sourceValue = 'CI_Objective';
        } else if (isLighthouseExecution) {
          sourceValue = 'LHE';
        } else if (isSparcKpi) {
          sourceValue = 'SPARC';
        } else if (sourceValue) {
          // Try to map from display text to backend value
          sourceValue = SOURCE_TO_BACKEND_MAPPING[sourceValue] || sourceValue.replace(/\s+/g, '_');
        }
        
        // Transform payload to match new API structure
        finalPayload = {
          title: payload.title,
          action_plan_owner_gpid: payload.action_plan_owner_gpid,
          action_plan_owner_email: payload.action_plan_owner_email,
          action_plan_owner_role: payload.action_plan_owner_role,
          action_plan_owner_role_id: payload.action_plan_owner_role_id,
          type: payload.type,
          priority: payload.priority,
          assignee_gpid: payload.assignee_gpid,
          assignee_email: payload.assignee_email,
          assignee_role: payload.assignee_role,
          assignee_role_id: payload.assignee_role_id,
          location: payload.location,
          period: payload.period,
          week: payload.week,
          start_date: payload.start_date,
          target_date: payload.target_date,
          action_plan_description: payload.action_plan_description,
          frontline_employee_involvement: payload.frontline_employee_involvement,
          involved_employee_gpids: payload.employee_gpid || undefined,
          involved_employee_names: payload.employee_name || undefined,
          capabilities: payload.capabilities || [],
          session_id: standupSessionId,
          //source_id: isEditMode && standupId ? standupId : undefined,
          created_by: payload.created_by,
          created_by_email: payload.created_by_email,
          created_by_role_id: payload.created_by_role_id,
          clientTimestamp: payload.clientTimestamp,
          is_standup: true,
          is_source: true,
          top_opportunity: isTopOpportunity ?? false,
          source: sourceValue,
          notes: payload.notes || undefined,
        };
        
        // Remove undefined fields
        Object.keys(finalPayload).forEach((k) => finalPayload[k] === undefined && delete finalPayload[k]);
        
        console.log('Creating action plan from lighthouse task with new API endpoint:', endpoint);
        console.log('isContinuousImprovement:', isContinuousImprovement);
        console.log('Source value:', sourceValue);
        console.log('Payload:', finalPayload);
      }
      
      // Make the API call
      const clientTimezone = getTimezoneOffsetString();
      const endpointWithTimezone = `${endpoint}?clientTimezone=${encodeURIComponent(clientTimezone)}`;
      const res = await apiClient(api, endpointWithTimezone, 'POST', finalPayload);

      // Dispatch success toast to Redux (persist a consistent message)
      dispatch(setToastNotification({
        header: '',
        subtext: 'Action plan has been created',
        variant: 'success',
        position: 'top',
        duration: 4000,
      }));

      const result = { success: true, data: res };

      // Call the onSubmit callback to trigger parent navigation
      if (onSubmit) {
        onSubmit(result);
      }

      return result;
    } catch (err: any) {
      console.error('Failed to create action plan', err);

      const rawDetails = err?.response?.data?.details;
      // Only use details if it's an actual object, not a string or primitive
      // Fix: When API returns 500 errors with details as a string (e.g., "Transaction has been aborted."),
      // Object.keys() would treat the string as an array and return character indices ["0","1","2",...],
      // causing the UI to display "Required fields are missing: 0, 1, 2, 3..." instead of the actual error
      const errorDetails = (rawDetails && typeof rawDetails === 'object' && !Array.isArray(rawDetails)) ? rawDetails : {};
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to create action plan';

      // Parse backend errors robustly and build per-field errors
      let fullErrorMessage = errorMessage;
      const fieldErrors: Record<string, string> = {};

      // Helper to normalize keys (e.g. 'start_date' -> 'start_date')
      const normalizeKey = (raw: string) => raw.replace(/[^a-z0-9_]/gi, '').toLowerCase();

      // 1) If errorDetails contains explicit field keys, use them
      if (errorDetails && Object.keys(errorDetails).length > 0) {
        Object.entries(errorDetails).forEach(([k, v]) => {
          const keyLower = normalizeKey(k);

          // Handle special container fields from some APIs
          if (keyLower === 'missingfields' || keyLower === 'missing_fields' || keyLower.includes('missing')) {
            // v may be a comma-separated string or array
            const vals = Array.isArray(v) ? v : String(v).split(/[,\s]+/);
            vals.forEach((f: any) => {
              const kk = normalizeKey(String(f));
              if (kk) fieldErrors[kk] = 'This field is required';
            });
          } else {
            // Use backend-provided message if available, otherwise default
            if (Array.isArray(v)) fieldErrors[keyLower] = v.join(', ');
            else if (typeof v === 'string') fieldErrors[keyLower] = v;
            else fieldErrors[keyLower] = String(v);
          }
        });
      } else {
        // 2) Try to parse CSV of missing fields from the errorMessage
        // Example message: "Required fields are missing: source,type,priority"
        const match = /missing[:]?\s*([a-z0-9_,\s]+)/i.exec(errorMessage);
        if (match && match[1]) {
          const vals = match[1].split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
          vals.forEach((f) => {
            const kk = normalizeKey(f);
            if (kk) fieldErrors[kk] = 'This field is required';
          });
        }
        // 3) Also check common location in errorDetails.MissingFields if present but top-level details empty
        const missingFromDetails = err?.response?.data?.details?.MissingFields || err?.response?.data?.details?.missingFields;
        if (missingFromDetails) {
          const vals = Array.isArray(missingFromDetails) ? missingFromDetails : String(missingFromDetails).split(/[,\s]+/);
          vals.forEach((f: any) => {
            const kk = normalizeKey(String(f));
            if (kk) fieldErrors[kk] = 'This field is required';
          });
        }
      }

      // Map known API keys to friendly labels for banner
      const fieldLabelMap: Record<string, string> = {
        source: 'Source',
        type: 'Type',
        priority: 'Priority',
        assignee_gpid: 'Assignee',
        assignee_email: 'Assignee Email',
        assignee_role: 'Assignee Role',
        assignee_role_id: 'Assignee Role ID',
        start_date: 'Start Date',
        target_date: 'Target Date',
        employee_gpid: 'Employee',
      };

      const missingKeys = Object.keys(fieldErrors);

      // Aggregate assignee-related errors into a single 'assignee' key
      const assigneeRelatedKeys = missingKeys.filter(k => k.startsWith('assignee'));
      if (assigneeRelatedKeys.length > 0) {
        const combined = assigneeRelatedKeys
          .map(k => fieldErrors[k])
          .filter(Boolean)
          .join('; ');
        // Set a single 'assignee' message and remove the individual keys
        fieldErrors['assignee'] = combined || 'Assignee is required';
        assigneeRelatedKeys.forEach(k => delete fieldErrors[k]);
      }

      // Aggregate employee-related backend keys into single 'employee' key
      const employeeRelatedKeys = Object.keys(fieldErrors).filter(k => k.startsWith('employee'));
      if (employeeRelatedKeys.length > 0) {
        const combinedEmp = employeeRelatedKeys.map(k => fieldErrors[k]).filter(Boolean).join('; ');
        fieldErrors['employee'] = combinedEmp || 'Employee Name is required';
        employeeRelatedKeys.forEach(k => delete fieldErrors[k]);
      }

      // Determine if this is actually a validation error or a general server error
      let isValidationError = false;
      
      if (missingKeys.length > 0) {
        const friendlyNames = missingKeys.map(k => fieldLabelMap[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
        fullErrorMessage = `Required fields are missing: ${friendlyNames.join(', ')}`;
        setValidationErrors(fieldErrors);
        isValidationError = true;
      } else {
        // Fallback: try parsing a comma-separated list from the top-level errorMessage
        const possibleList = (errorMessage || '').split(':').pop() || '';
        const csvCandidates = possibleList.split(',').map((s: string) => s.trim()).filter(Boolean);
        const parsedFromMessage: Record<string,string> = {};
        csvCandidates.forEach((c: string) => {
          const kk = normalizeKey(c);
          if (!kk) return;
          // accept keys that look like field names (contain underscore or match known map)
          if (kk.includes('_') || fieldLabelMap[kk]) {
            parsedFromMessage[kk] = 'This field is required';
          }
        });

        if (Object.keys(parsedFromMessage).length > 0) {
          const friendlyNames = Object.keys(parsedFromMessage).map(k => fieldLabelMap[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
          fullErrorMessage = `Required fields are missing: ${friendlyNames.join(', ')}`;
          setValidationErrors(parsedFromMessage);
          isValidationError = true;
        }
      }

      // Use appropriate error title based on whether this is a validation error or server error
      const errorTitle = isValidationError ? 'Validation Error' : 'Error';
      
      setSubmissionError({
        title: errorTitle,
        message: fullErrorMessage,
        details: errorDetails || null,
      });

      // Dispatch error toast to Redux
      dispatch(setToastNotification({
        header: errorTitle,
        subtext: fullErrorMessage,
        variant: 'error',
        position: 'top',
        duration: 4000,
      }));

      return { success: false, error: submissionError };
    } finally {
      setLoading(false);
    }
  };

  // Clear per-field validation errors when user updates the corresponding fields
  useEffect(() => {
    if (selectedSource) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.source; return next; });
    }
  }, [selectedSource]);

  useEffect(() => {
    if (selectedType) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.type; return next; });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedPriority) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.priority; return next; });
    }
  }, [selectedPriority]);

  useEffect(() => {
    if (selectedAssignee) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next.assignee;
        return next;
      });
    }
  }, [selectedAssignee]);

  useEffect(() => {
    if (startDate) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.start_date; return next; });
    }
  }, [startDate]);

  useEffect(() => {
    if (targetDate) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.target_date; return next; });
    }
  }, [targetDate]);

  // Clear aggregated employee error when selectedEmployee changes
  useEffect(() => {
    if (selectedEmployee) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.employee; delete next.employee_gpid; return next; });
    }
  }, [selectedEmployee]);

  return {
    // Form fields
    title,
    setTitle,
    selectedSource,
    setSelectedSource,
    selectedType,
    setSelectedType,
    selectedPriority,
    setSelectedPriority,
    selectedOwner,
    setSelectedOwner,
    selectedAssignee,
    setSelectedAssignee,
    startDate,
    setStartDate,
    targetDate,
    setTargetDate,
    handleTargetDateChange,
    actionPlan,
    setActionPlan,
    notes,
    setNotes,
    frontlineInvolvement,
    setFrontlineInvolvement,
    selectedEmployee,
    setSelectedEmployee,
    selectedArea,
    setSelectedArea,
    areaOptions,

    // States
    isView,
    loading,
    dateValidationError,
    submissionError,
    validationErrors,
    setSubmissionError,
    assigneeLoading,
    employeeLoading,
    isCreateDisabled,

    // Options
    sourceOptions: SOURCE_OPTIONS,
    typeOptions: TYPE_OPTIONS,
    priorityOptions: PRIORITY_OPTIONS,
    ownerOption,
    assigneeList,
    employeeList,

    // Handlers
    handleSubmit,
    formatDisplayDate,
  };
};

